import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, Dialog, DialogActions, DialogContent, IconButton, Toolbar, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { DynamicQuestionnaire } from 'benchmarking-domain/dynamic-questionnaire';
import { PermissionsHelper } from 'benchmarking-domain/permissions-helper';
import type Project from 'benchmarking-domain/project';
import ProjectProgressStatistics from 'benchmarking-domain/project-progress-statistics';
import ProjectSection from 'benchmarking-domain/project-section';
import Questionnaire from 'benchmarking-domain/questionnaire';
import KpiViewer from 'components/client/projects/kpi-viewer/kpi-viewer';
import NormalizationViewer from 'components/client/projects/normalization-viewer/normalization-viewer';
import useApi from 'hooks/use-api';
import { enqueueSnackbar } from 'notistack';
import { paths } from 'paths';
import { useNavigate, useParams } from 'react-router-dom';

import { RenderForRole } from '../../../components/auth/render-for-roles';
import { ProjectForm } from '../../../components/client/projects/project-form';
import { QuestionnaireForm } from '../../../components/client/projects/questionnaire-form';
import { useOrganisation } from '../../../hooks/use-organisation';
import { useUser } from '../../../hooks/use-user';
import { ProgressSummary } from './progress-summary';

const ProjectFormMemo = React.memo(ProjectForm);
const QuestionnaireFormMemo = React.memo(QuestionnaireForm);
const ProgressSummaryMemo = React.memo(ProgressSummary);
const KpiViewerMemo = React.memo(KpiViewer);
const ProjectHistoryLazyMemo = React.lazy(() => import('components/client/projects/project-history'));

export default function ProjectDetails(): React.JSX.Element {
  const { organisationId } = useOrganisation();
  const { user } = useUser();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const {
    subscribeProject,
    subscribeQuestionnaires,
    subscribeDynamicQuestionnaires,
    subscribeProjectProgressStatistics,
    subscribeProjectSections,
    saveProject,
  } = useApi();
  const [project, setProject] = useState<Project | undefined>();
  const [projectProgressStatistics, setProjectProgressStatistics] = useState<ProjectProgressStatistics | undefined>();
  const [projectProgressStatisticsLoaded, setProjectProgressStatisticsLoaded] = useState<boolean>(false);
  const [projectSections, setProjectSections] = useState<ProjectSection[] | undefined>();
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[] | undefined>();
  const [questionnairesLoaded, setQuestionnairesLoaded] = useState<boolean>(false);
  const [dynamicQuestionnaires, setDynamicQuestionnaires] = useState<DynamicQuestionnaire[] | undefined>();
  const [dynamicQuestionnairesLoaded, setDynamicQuestionnairesLoaded] = useState<boolean>(false);
  const [displayKpiViewer, setDisplayKpiViewer] = useState<boolean>(false);
  const [displayNormalizationViewer, setDisplayNormalizationViewer] = useState<boolean>(false);
  const [displayProjectHistory, setDisplayProjectHistory] = useState<boolean>(false);

  const handleOpenKpiViewer = useCallback(() => setDisplayKpiViewer(true), []);
  const handleCloseKpiViewer = useCallback(() => setDisplayKpiViewer(false), []);

  const handleOpenNormalizationViewer = useCallback(() => setDisplayNormalizationViewer(true), []);
  const handleCloseNormalizationViewer = useCallback(() => setDisplayNormalizationViewer(false), []);

  const handleOpenProjectHistory = useCallback(() => setDisplayProjectHistory(true), []);
  const handleCloseProjectHistory = useCallback(() => setDisplayProjectHistory(false), []);

  useEffect(() => {
    if (!organisationId || !projectId) {
      navigate(paths.errors.notFound, { replace: true });
      return;
    }

    const unsubscribeProject = subscribeProject(organisationId, projectId, setProject);

    const unsubscribeProjectProgressStatistics = subscribeProjectProgressStatistics(
      organisationId,
      projectId,
      (fetchedProjectProgressStatistics) => {
        setProjectProgressStatistics(fetchedProjectProgressStatistics);
        setProjectProgressStatisticsLoaded(true);
      }
    );

    return () => {
      unsubscribeProject();
      unsubscribeProjectProgressStatistics();
    };
  }, [navigate, organisationId, projectId, subscribeProject, subscribeProjectProgressStatistics]);

  useEffect(() => {
    if (!project) {
      return;
    }

    const unsubscribeQuestionnaires = subscribeQuestionnaires(project.frameworkId, true, (questionnaires) => {
      setQuestionnaires(questionnaires);
      setQuestionnairesLoaded(true);
    });

    const unsubscribeDynamicQuestionnaires = subscribeDynamicQuestionnaires(
      project.frameworkId,
      true,
      (dynamicQuestionnaires) => {
        setDynamicQuestionnaires(dynamicQuestionnaires);
        setDynamicQuestionnairesLoaded(true);
      }
    );

    return () => {
      unsubscribeQuestionnaires();
      unsubscribeDynamicQuestionnaires();
    };
  }, [project, subscribeQuestionnaires, subscribeDynamicQuestionnaires]);

  useEffect(() => {
    if (!project || !organisationId) {
      return;
    }

    const unsubscribeProjectSections = subscribeProjectSections(organisationId, project.id, (sections) => {
      if (sections) {
        setProjectSections(sections);
      }
    });

    return () => unsubscribeProjectSections();
  }, [organisationId, project, subscribeProjectSections]);

  const submitProjectChanges = useCallback(
    async (data: Project) => {
      if (!organisationId) {
        enqueueSnackbar('Organisation not set!', { variant: 'error' });
        return;
      }

      try {
        await saveProject(organisationId, { ...data, id: projectId! });
        enqueueSnackbar('Project saved!', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Project save failed! ' + error, { variant: 'error' });
      }
    },
    [saveProject, organisationId, projectId]
  );

  if (
    !projectId ||
    !project ||
    !questionnairesLoaded ||
    !dynamicQuestionnairesLoaded ||
    !projectProgressStatisticsLoaded
  ) {
    return <></>;
  }

  return (
    <Stack spacing={3}>
      <ProjectFormMemo key={projectId} project={project} onSubmit={submitProjectChanges} />

      {PermissionsHelper.hasUserAtLeastRole('projectManager', user, project) && (
        <Card>
          <CardHeader title="Actions" />
          <Divider />
          <CardContent>
            <Stack direction={'row'} spacing={1}>
              <Button variant="outlined" onClick={handleOpenKpiViewer}>
                Open KPI viewer
              </Button>
              <Dialog open={displayKpiViewer} onClose={handleCloseKpiViewer} maxWidth="lg" fullWidth fullScreen>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AppBar sx={{ position: 'relative', borderRadius: '8px' }}>
                    <Toolbar>
                      <IconButton edge="start" color="inherit" onClick={handleCloseKpiViewer} aria-label="close">
                        <CloseIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        KPI Viewer
                      </Typography>
                      <Button autoFocus color="inherit" onClick={handleCloseKpiViewer}>
                        Close
                      </Button>
                    </Toolbar>
                  </AppBar>

                  <KpiViewerMemo project={project} projectSections={projectSections} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseKpiViewer}>Close</Button>
                </DialogActions>
              </Dialog>

              <Button variant="outlined" onClick={handleOpenNormalizationViewer}>
                View normalization logic
              </Button>
              <Dialog
                open={displayNormalizationViewer}
                onClose={handleCloseNormalizationViewer}
                maxWidth="lg"
                fullWidth
                fullScreen
              >
                <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AppBar sx={{ position: 'relative', borderRadius: '8px' }}>
                    <Toolbar>
                      <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleCloseNormalizationViewer}
                        aria-label="close"
                      >
                        <CloseIcon />
                      </IconButton>
                      <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        Normalization details
                      </Typography>
                      <Button autoFocus color="inherit" onClick={handleCloseNormalizationViewer}>
                        Close
                      </Button>
                    </Toolbar>
                  </AppBar>

                  <NormalizationViewer project={project} projectProgressStatistics={projectProgressStatistics} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseNormalizationViewer}>Close</Button>
                </DialogActions>
              </Dialog>

              <RenderForRole roles={['superAdmin']}>
                <Button variant="outlined" onClick={handleOpenProjectHistory}>
                  View history for all input values in this project (Admin only)
                </Button>
                <Dialog
                  open={displayProjectHistory}
                  onClose={handleCloseProjectHistory}
                  maxWidth="lg"
                  fullWidth
                  fullScreen
                >
                  <DialogContent>
                    <AppBar sx={{ position: 'relative', borderRadius: '8px' }}>
                      <Toolbar>
                        <IconButton edge="start" color="inherit" onClick={handleCloseProjectHistory} aria-label="close">
                          <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                          Project history - {project?.name}
                        </Typography>
                        <Button autoFocus color="inherit" onClick={handleCloseProjectHistory}>
                          Close
                        </Button>
                      </Toolbar>
                    </AppBar>

                    <Box sx={{ marginTop: 2 }}>
                      <React.Suspense fallback={'Loading...'}>
                        <ProjectHistoryLazyMemo key={projectId} projectId={projectId} />
                      </React.Suspense>
                    </Box>
                  </DialogContent>
                </Dialog>
              </RenderForRole>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader title="Progress summary" />
        <Divider />
        <CardContent>
          <ProgressSummaryMemo
            project={project}
            projectSections={projectSections}
            projectProgressStatistics={projectProgressStatistics}
            questionnaires={questionnaires}
          />
        </CardContent>
      </Card>

      {organisationId &&
        projectId &&
        questionnaires?.map((questionnaire) => {
          return (
            <QuestionnaireFormMemo
              key={questionnaire.id}
              organisationId={organisationId}
              project={project}
              projectSections={projectSections}
              projectProgressStatistics={projectProgressStatistics}
              questionnaire={questionnaire}
              dynamicQuestionnaires={dynamicQuestionnaires}
            />
          );
        })}
    </Stack>
  );
}
